import { Transaction, Holding, PriceMap } from '../../types';
import { computeHoldings } from '../../utils/calculations';
import { walletScopedTransactions } from '../../utils/walletScope';
import { IDENTITY_RATES } from '../../utils/displayRates';
import type { DisplayRateTable } from '../../utils/displayRates';

/**
 * Per-coin transaction stats for the redesigned Transactions tab.
 *
 * Holding figures (current amount, value, avg buy, unrealized P&L) come from the
 * live `Holding` when the position is still open. Realized P&L, invested and
 * proceeds are walked from the raw transactions so that fully-sold positions
 * (which never appear in `holdings`) still surface their realized result.
 *
 * Cost-basis math mirrors `computeHoldings` in utils/calculations.ts: SELLs shrink
 * the running average cost proportionally (not FIFO/LIFO), and TRANSFER network
 * fees add to cost basis without changing the coin count.
 *
 * Trading fees follow the same split as the CGT engine: a BUY fee joins cost
 * basis (and `invested`), a SELL fee comes off proceeds. A fee is never absent-
 * defaulted to anything but zero, so rows predating fees read exactly as before.
 */
export interface CoinTxnStat {
  coinId: string;
  symbol: string;
  name: string;
  txnCount: number;
  amount: number;          // current quantity held (0 if fully sold)
  value: number;           // current holding value in USD
  avgBuyPrice: number;     // average buy price of the remaining position
  invested: number;        // sum of all BUY totals
  proceeds: number;        // sum of all SELL totals
  realizedPnl: number;     // realized gain/loss across all SELLs
  unrealizedPnl: number;   // current value − remaining cost basis
  netPnl: number;          // realized + unrealized
  /** Whether a P&L percentage is meaningful to show (held, or has realized a sale). */
  hasPnlPercent: boolean;
  /** Unrealized % while held, else realized % of the cost basis sold. */
  pnlPercent: number;
  held: boolean;
  /**
   * The same figures in the display currency, under the same three-class split as
   * `HoldingDisplay`: `invested` / `proceeds` / `realizedPnl` are historical
   * (each transaction at its own date), `value` is today's, and the P&L figures
   * that compare them therefore include currency movement.
   */
  display: {
    invested: number;       // class A
    proceeds: number;       // class A
    realizedPnl: number;    // class A (both legs historical)
    value: number;          // class B
    avgBuyPrice: number;    // class A
    unrealizedPnl: number;  // class C
    netPnl: number;         // class C
    pnlPercent: number;     // class C
  };
}

export function computeCoinTxnStat(
  coinId: string,
  allTxs: Transaction[],
  holding?: Holding,
  /** See computeHoldings: defaults to identity, so display === USD when omitted. */
  displayRates: DisplayRateTable = IDENTITY_RATES,
): CoinTxnStat {
  const txs = allTxs
    .filter((t) => t.coinId === coinId)
    .sort((a, b) => a.timestamp - b.timestamp);

  let amount = 0;
  let costBasis = 0;
  let realizedPnl = 0;
  let costBasisSold = 0;
  let invested = 0;
  let proceeds = 0;
  let symbol = '';
  let name = '';
  // Display-currency twins, each accumulated at its own transaction's date.
  let costBasisD = 0;
  let realizedPnlD = 0;
  let costBasisSoldD = 0;
  let investedD = 0;
  let proceedsD = 0;

  for (const tx of txs) {
    symbol = tx.symbol;
    name = tx.name;
    // Trading fee in USD, zero when absent (every pre-fee row), so existing
    // figures are untouched.
    const feeUsd = tx.feeUsd && tx.feeUsd > 0 ? tx.feeUsd : 0;
    // One lookup per transaction, memoised by date inside the table.
    const perUsd = displayRates.perUsdOn(tx.date);

    if (tx.type === 'BUY') {
      // The fee joins cost basis and what was invested — it is money spent to
      // acquire the position.
      const costUsd = tx.amount * tx.price + feeUsd;
      costBasis += costUsd;
      amount += tx.amount;
      invested += costUsd;
      costBasisD += costUsd * perUsd;
      investedD += costUsd * perUsd;
    } else if (tx.type === 'TRANSFER') {
      if (tx.networkFee && tx.networkFee > 0) {
        costBasis += tx.networkFee;
        costBasisD += tx.networkFee * perUsd;
      }
    } else {
      // SELL — realize against the running average cost. The sell fee comes off
      // proceeds, so it reduces the realised gain without touching the cost basis
      // of whatever is still held.
      const avgCost = amount > 0 ? costBasis / amount : 0;
      const avgCostD = amount > 0 ? costBasisD / amount : 0;
      const costRemoved = avgCost * tx.amount;
      const costRemovedD = avgCostD * tx.amount;
      const netProceeds = tx.amount * tx.price - feeUsd;
      // Proceeds convert at the SELL's date and cost at the buys' dates, so a
      // realised gain here already includes the FX movement over the hold — the
      // same treatment the CGT engine applies per leg.
      const netProceedsD = netProceeds * perUsd;
      realizedPnl += netProceeds - costRemoved;
      realizedPnlD += netProceedsD - costRemovedD;
      costBasisSold += costRemoved;
      costBasisSoldD += costRemovedD;
      proceeds += netProceeds;
      proceedsD += netProceedsD;
      amount = Math.max(0, amount - tx.amount);
      costBasis = Math.max(0, amount * avgCost);
      costBasisD = Math.max(0, amount * avgCostD);
    }
  }

  const held = !!holding && holding.amount > 0.000001;
  const value = held ? holding!.totalValue : 0;
  const avgBuyPrice = held ? holding!.avgBuyPrice : 0;
  const curAmount = held ? holding!.amount : 0;
  const unrealizedPnl = held ? holding!.pnl : 0;
  const netPnl = realizedPnl + unrealizedPnl;

  const hasPnlPercent = held ? holding!.totalCost > 0 : costBasisSold > 0;
  const pnlPercent = held
    ? holding!.pnlPercent
    : costBasisSold > 0
      ? (realizedPnl / costBasisSold) * 100
      : 0;

  return {
    coinId,
    symbol,
    name,
    txnCount: txs.length,
    amount: curAmount,
    value,
    avgBuyPrice,
    invested,
    proceeds,
    realizedPnl,
    unrealizedPnl,
    netPnl,
    hasPnlPercent,
    pnlPercent,
    held,
    display: {
      invested: investedD,
      proceeds: proceedsD,
      realizedPnl: realizedPnlD,
      value: held ? holding!.display.totalValue : 0,
      avgBuyPrice: held ? holding!.display.avgBuyPrice : 0,
      unrealizedPnl: held ? holding!.display.pnl : 0,
      netPnl: realizedPnlD + (held ? holding!.display.pnl : 0),
      pnlPercent: held
        ? holding!.display.pnlPercent
        : costBasisSoldD > 0
          ? (realizedPnlD / costBasisSoldD) * 100
          : 0,
    },
  };
}

/**
 * Build one stat per coin that has any transaction history, sorted by current
 * holding value descending (open positions first, fully-sold positions after).
 */
export function buildCoinTxnStats(
  transactions: Transaction[],
  holdings: Holding[],
  displayRates: DisplayRateTable = IDENTITY_RATES,
): CoinTxnStat[] {
  const holdingById = new Map(holdings.map((h) => [h.coinId, h]));
  const coinIds: string[] = [];
  const seen = new Set<string>();
  for (const tx of transactions) {
    if (!seen.has(tx.coinId)) {
      seen.add(tx.coinId);
      coinIds.push(tx.coinId);
    }
  }
  return coinIds
    .map((id) => computeCoinTxnStat(id, transactions, holdingById.get(id), displayRates))
    .sort((a, b) => b.value - a.value || b.txnCount - a.txnCount);
}

// ── Wallet filter ──────────────────────────────────────────────────────────────

/**
 * Whether `tx` belongs in the Transactions tab's wallet view: bought or sold in
 * it, or transferred into or out of it. `''` is All Wallets. The one test both
 * the transaction lists and the coin list use, so they cannot disagree.
 */
export function txMatchesWallet(tx: Transaction, wallet: string): boolean {
  return !wallet || tx.wallet === wallet || tx.fromWallet === wallet || tx.toWallet === wallet;
}

/**
 * The coin list for one wallet: only coins with a transaction in it, each with
 * that wallet's transaction count, value and P&L. Value and P&L come from the
 * wallet's own holdings (transfers in and out as virtual buys and sells, the
 * same scoping the Holdings wallet filter uses), so they match that table.
 * With All Wallets this is `buildCoinTxnStats` unchanged.
 */
export function buildWalletCoinTxnStats(
  transactions: Transaction[],
  holdings: Holding[],
  prices: PriceMap,
  wallet: string,
  displayRates: DisplayRateTable = IDENTITY_RATES,
): CoinTxnStat[] {
  if (!wallet) return buildCoinTxnStats(transactions, holdings, displayRates);
  const scoped = walletScopedTransactions(transactions, wallet);
  const stats = buildCoinTxnStats(scoped, computeHoldings(scoped, prices, displayRates), displayRates);
  // Count real rows, not the scoped ones: a transfer between two accounts of the
  // same wallet becomes two virtual legs there but is one transaction.
  const counts = new Map<string, number>();
  for (const tx of transactions) {
    if (txMatchesWallet(tx, wallet)) counts.set(tx.coinId, (counts.get(tx.coinId) ?? 0) + 1);
  }
  return stats
    .filter((s) => counts.has(s.coinId))
    .map((s) => ({ ...s, txnCount: counts.get(s.coinId)! }));
}
